import React, { useCallback, useEffect, useState } from 'react';
import { MoonStar, RotateCcw, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/* Admin → Fabric Colors: regenerate the AI room renders of blackout colours as truly opaque shades.
   A restore point (the original renders) is kept per colour and can be put back at any time. */
export const BlackoutRendersPanel = ({ token, isRunning, onJobStarted, refreshKey }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/fabrics/blackout-renders/status`, { headers });
      setStatus(data);
    } catch { /* panel is optional */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey, isRunning]);

  const regenerate = async (limit) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/fabrics/blackout-renders/regenerate`, limit ? { limit } : {}, { headers });
      onJobStarted?.(data);
      toast.info(`Regenerating ${data.total} blackout scene${data.total === 1 ? '' : 's'} as opaque shades`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start blackout regeneration');
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!window.confirm('Put the original AI renders back for every regenerated blackout colour? The configurator will then use the opaque room engine for them again.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/fabrics/blackout-renders/restore`, {}, { headers });
      toast.success(`Restored original renders for ${data.restored_colors} colour${data.restored_colors === 1 ? '' : 's'}`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  if (!status || status.total_colors === 0) return null;
  const est = status.estimate || {};
  const disabled = busy || isRunning;

  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-900 text-white px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="blackout-renders-panel">
      <div className="flex items-start gap-3">
        <MoonStar className="w-5 h-5 mt-0.5 text-amber-400 shrink-0" />
        <div className="text-sm">
          <p className="font-medium">Blackout AI renders</p>
          <p className="text-neutral-300" data-testid="blackout-renders-status">
            {status.regenerated_colors} of {status.total_colors} colours regenerated as opaque
            {status.pending_colors > 0 && ` · ${status.pending_scenes} scene${status.pending_scenes === 1 ? '' : 's'} pending (~$${est.estimated_cost_usd}, ~${est.estimated_minutes} min)`}
            {status.pending_colors === 0 && ' · all done'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {status.pending_colors > 0 && (
          <>
            <Button size="sm" variant="outline" onClick={() => regenerate(5)} disabled={disabled} className="gap-1.5 border-neutral-600 bg-transparent text-white hover:bg-neutral-800 hover:text-white" data-testid="blackout-pilot-btn">
              <Sparkles className="w-3.5 h-3.5" /> Pilot (5 colours)
            </Button>
            <Button size="sm" onClick={() => regenerate()} disabled={disabled} className="gap-1.5 bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="blackout-regenerate-all-btn">
              <Sparkles className="w-3.5 h-3.5" /> Regenerate remaining ({status.pending_colors})
            </Button>
          </>
        )}
        {status.restorable_colors > 0 && (
          <Button size="sm" variant="ghost" onClick={restore} disabled={disabled} className="gap-1.5 text-neutral-300 hover:bg-neutral-800 hover:text-white" data-testid="blackout-restore-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Restore originals ({status.restorable_colors})
          </Button>
        )}
      </div>
    </div>
  );
};

export default BlackoutRendersPanel;
